import React, { useState, useEffect } from 'react';
import { supabase } from './supabaseClient';
import { Search, Loader2, Brain } from 'lucide-react';
import logo from './logo.png';

const EXAMPLES = [
  "Similar companies we've seen",
  "Why did we pass on deals?",
  "Best performing sectors",
  "Companies with strong traction",
];

export default function FundMemory() {
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [answer, setAnswer] = useState(null);
  const [sources, setSources] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [session, setSession] = useState(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
    });
  }, []);

  const handleSubmit = async () => {
    if (!query.trim() || loading) return;
    setLoading(true);
    setAnswer(null);
    setSources([]);
    setHasSearched(true);
    try {
      const res = await fetch('/api/rag', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`
        },
        body: JSON.stringify({ operation: 'query', query })
      });
      const data = await res.json();
      setAnswer(data.answer);
      setSources(data.sources || []);
    } catch {
      setAnswer('Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ maxWidth: 800, margin: '0 auto', padding: '32px 0', fontFamily: "'DM Sans', system-ui, sans-serif" }}>

      {/* HEADER */}
      <div style={{ marginBottom: 40, textAlign: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 14, marginBottom: 12 }}>
          <img src={logo} style={{ width: 28, height: 28 }} alt="Binos" />
          <div style={{ width: 1, height: 28, background: 'rgba(255,255,255,0.08)' }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 36, height: 36, background: 'rgba(220,38,38,0.12)', border: '1px solid rgba(220,38,38,0.2)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Brain style={{ width: 18, height: 18, color: '#dc2626' }} />
            </div>
            <h1 style={{ fontSize: 26, fontWeight: 900, color: '#fff', margin: 0, letterSpacing: '-0.5px' }}>Fund Memory</h1>
          </div>
        </div>
        <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.35)', margin: 0, fontWeight: 500 }}>
          Ask anything about your fund's deal history
        </p>
      </div>

      {/* SEARCH BOX */}
      <div style={{ background: '#1a1a1a', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 16, padding: '20px', marginBottom: 24 }}>
        <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
          <div style={{ position: 'relative', flex: 1 }}>
            <Search style={{ position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', width: 15, height: 15, color: 'rgba(255,255,255,0.25)' }} />
            <input
              type="text"
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSubmit()}
              placeholder="e.g. Have we seen any B2B SaaS companies with strong traction?"
              style={{
                width: '100%',
                paddingLeft: 46,
                paddingRight: 16,
                paddingTop: 14,
                paddingBottom: 14,
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: 12,
                outline: 'none',
                fontSize: 14,
                fontWeight: 500,
                color: '#fff',
                fontFamily: 'inherit',
                boxSizing: 'border-box',
              }}
              onFocus={e => e.target.style.borderColor = '#dc2626'}
              onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.08)'}
            />
          </div>
          <button
            onClick={handleSubmit}
            disabled={loading || !query.trim()}
            style={{
              padding: '14px 22px',
              background: '#dc2626',
              border: 'none',
              borderRadius: 12,
              fontSize: 13,
              fontWeight: 800,
              color: '#fff',
              cursor: loading || !query.trim() ? 'not-allowed' : 'pointer',
              fontFamily: 'inherit',
              opacity: loading || !query.trim() ? 0.55 : 1,
              whiteSpace: 'nowrap',
              transition: 'opacity 0.15s',
            }}
          >
            Ask Binos →
          </button>
        </div>

        {/* Example pills */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {EXAMPLES.map((ex, i) => (
            <button
              key={i}
              onClick={() => setQuery(ex)}
              style={{
                padding: '6px 14px',
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.07)',
                borderRadius: 100,
                fontSize: 11,
                fontWeight: 600,
                color: 'rgba(255,255,255,0.4)',
                cursor: 'pointer',
                fontFamily: 'inherit',
                transition: 'all 0.15s',
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(220,38,38,0.35)'; e.currentTarget.style.color = '#dc2626'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.07)'; e.currentTarget.style.color = 'rgba(255,255,255,0.4)'; }}
            >
              {ex}
            </button>
          ))}
        </div>
      </div>

      {/* LOADING */}
      {loading && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, padding: '48px 0' }}>
          <Loader2 style={{ width: 20, height: 20, color: '#dc2626' }} className="animate-spin" />
          <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.4)', margin: 0, fontWeight: 500 }}>
            Searching your fund's history...
          </p>
        </div>
      )}

      {/* ANSWER */}
      {!loading && answer && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{
            background: '#1a1a1a',
            borderLeft: '3px solid #dc2626',
            borderRadius: 12,
            padding: '22px 26px',
          }}>
            <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.8)', margin: 0, lineHeight: 1.75, fontWeight: 400, whiteSpace: 'pre-wrap' }}>
              {answer}
            </p>
          </div>

          {sources.length > 0 && (
            <div>
              <p style={{ fontSize: 9, fontWeight: 700, color: 'rgba(255,255,255,0.25)', textTransform: 'uppercase', letterSpacing: '0.14em', margin: '0 0 10px' }}>
                Based on these records:
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {sources.map((s, i) => (
                  <span
                    key={i}
                    style={{
                      padding: '5px 12px',
                      background: 'rgba(220,38,38,0.1)',
                      border: '1px solid rgba(220,38,38,0.18)',
                      borderRadius: 100,
                      fontSize: 11,
                      fontWeight: 600,
                      color: 'rgba(255,255,255,0.55)',
                    }}
                  >
                    {s.company_name} · {s.content_type} · {new Date(s.created_at).toLocaleDateString()}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* NO RESULTS */}
      {!loading && hasSearched && answer === null && (
        <div style={{ textAlign: 'center', padding: '64px 24px' }}>
          <div style={{ width: 56, height: 56, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
            <Brain style={{ width: 24, height: 24, color: 'rgba(255,255,255,0.1)' }} />
          </div>
          <p style={{ fontSize: 14, fontWeight: 700, color: 'rgba(255,255,255,0.2)', margin: '0 0 6px' }}>
            No relevant history found yet.
          </p>
          <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.15)', fontWeight: 500, margin: 0, lineHeight: 1.6 }}>
            Analyse some decks to build<br />your fund's memory.
          </p>
        </div>
      )}
    </div>
  );
}
